import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { Lock, AlertTriangle, CreditCard, Loader } from 'lucide-react';
import { functions } from '../../firebase';

export default function SubscriptionLocked({ companyData, companyId }) {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');

  const deleteAt = companyData?.shiftsDeleteAt
    ? (companyData.shiftsDeleteAt.toDate
        ? companyData.shiftsDeleteAt.toDate()
        : new Date(companyData.shiftsDeleteAt)
      ).toLocaleDateString()
    : null;

  const handleRefresh = async () => {
    if (!companyId) return;
    setRefreshing(true);
    setRefreshMessage('');

    try {
      const refreshSubscriptionStatus = httpsCallable(functions, 'shifts_refreshSubscriptionStatus');
      const result = await refreshSubscriptionStatus({ companyId });
      const status = result.data?.status;
      if (['active', 'trialing'].includes(status)) {
        setRefreshMessage(t('subscription.refreshFound', 'Subscription found. Access is unlocking now.'));
      } else {
        setRefreshMessage(
          t('subscription.refreshStillInactive', 'Stripe still reports this subscription as {{status}}.', {
            status: status || t('subscription.inactive', 'inactive'),
          })
        );
      }
    } catch (err) {
      console.error('Locked screen refresh error:', err);
      setRefreshMessage(
        t(
          'subscription.refreshFailed',
          'We could not refresh the subscription yet. If you just paid, wait a few seconds and try again.'
        )
      );
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-6">
            <Lock className="w-8 h-8 text-red-400" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-3">
            {t('subscription.locked', 'Account Locked')}
          </h1>

          <p className="text-gray-300 mb-4">
            {t('subscription.lockedMessage',
              'Your BuildMyShifts subscription is not active. Features are restricted until you subscribe.'
            )}
          </p>

          {deleteAt && (
            <div className="flex items-center justify-center gap-2 text-amber-400 text-sm mb-6">
              <AlertTriangle className="w-4 h-4" />
              <span>
                {t('subscription.deleteWarning',
                  'Your data will be permanently deleted on {{date}}',
                  { date: deleteAt }
                )}
              </span>
            </div>
          )}

          <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
            <p className="text-gray-400 text-sm font-medium mb-2">
              {t('subscription.whileLocked', 'While locked you cannot:')}
            </p>
            <ul className="text-gray-500 text-sm space-y-1">
              <li>• {t('subscription.noCreateShifts', 'Create or edit shifts/rotas')}</li>
              <li>• {t('subscription.noClockIn', 'Use time clock')}</li>
              <li>• {t('subscription.noManageStaff', 'Manage staff or settings')}</li>
              <li>• {t('subscription.noSwaps', 'Process swap requests')}</li>
            </ul>
            <p className="text-gray-500 text-sm mt-2">
              {t('subscription.canView', 'You can still view existing rotas, historical data, and reports.')}
            </p>
          </div>

          {companyId && (
            <div className="mb-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center gap-2 border border-white/15 bg-white/5 hover:bg-white/10 text-white font-medium px-4 py-2 rounded-xl transition disabled:opacity-50"
              >
                {refreshing ? <Loader className="w-4 h-4 animate-spin" /> : null}
                {t('subscription.refreshAccessCta', 'I already paid, refresh access')}
              </button>
              {refreshMessage && (
                <p className="text-sm text-gray-300 mt-3">{refreshMessage}</p>
              )}
            </div>
          )}

          <Link
            to="/subscription"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-6 py-3 rounded-xl transition"
          >
            <CreditCard className="w-5 h-5" />
            {t('subscription.subscribeCta', 'Subscribe Now')}
          </Link>
        </div>
      </div>
    </div>
  );
}
